import { useDisclosure } from '@mantine/hooks';
import { notifications as mantineNotification } from '@mantine/notifications';
import {
	type ActorDto,
	isApiError,
	TalkingHeadActorEventIdn
} from '@newo/api-client';
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import {
	PARTNER_INDUSTRY,
	PATHS,
	runtimeConfig,
	USER_AGENT_VALUE
} from '@/constants';
import { useHostContext } from '@/context/HostContext';
import useQueryParams from '@/hooks/useQueryParams';
import type { AgentIntroTrainingSettings } from '@/pages/AgentIntro/components/TrainingSourceSelector/TrainingSourceSelector.type';
import { AgentIntroSourceType } from '@/pages/AgentIntro/components/TrainingSourceSelector/TrainingSourceSelector.type';
import { buildNavigatePath } from '@/routes';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime.ts';
import { partnerMetadata } from '@/services/PartnerMetadata.ts';
import { useAgentTraining } from '@/store/agent-training';
import useAppStore from '@/store/app';
import { AgentCommand } from '@/types/agent-realtime.ts';
import {
	AgentCreationStep,
	AgentTrainingStep,
	TScrapingStatus
} from '@/types/agent-training';
import { useActorSendEventMutation, useInitialize } from '@/utils/api';
import { buildTrainingStartPayload } from '@/utils/buildTrainingStartPayload';
import { isValid } from '@/utils/validation';

const useTraining = (actor?: ActorDto) => {
	const [isPending, { open: enableLoading, close: disableLoading }] =
		useDisclosure(false);

	const navigate = useNavigate();
	const { setExternalId, setCustomerData, setBusinessSource } = useAppStore(
		useShallow((state) => ({
			setExternalId: state.setExternalId,
			setCustomerData: state.setCustomerData,
			setBusinessSource: state.setBusinessSource
		}))
	);

	const { partner_idn, member_idn, localization } = useHostContext();
	const [_, setSearchParams] = useSearchParams();
	const { mutateAsync } = useInitialize(true, actor);
	const { mutate: sendActorEvent } = useActorSendEventMutation();

	const {
		setScrapingStatus,
		setAgentStep,
		setTrainingStep,
		setPartnerIdn,
		setMemberIdn
	} = useAgentTraining(
		useShallow((s) => ({
			setScrapingStatus: s.setScrapingStatus,
			setAgentStep: s.setAgentStep,
			setTrainingStep: s.setTrainingStep,
			setPartnerIdn: s.setPartnerIdn,
			setMemberIdn: s.setMemberIdn
		}))
	);

	const {
		params: {
			google_map_url: googleMapUrl,
			source: websiteUrl,
			industry,
			full_name: fullName,
			email,
			phone_number: phoneNumber,
			promo_code: promoCode,
			language,
			country_code: countryCode,
			external_account_number: externalAccountNumber,
			referral,
			comment,
			partner_idn: queryPartnerIdn,
			partner_member_idn: queryPartnerMemberIdn,
			account_managers: accountManagers,
			technical_owners: technicalOwners,
			notifications,
			external_owner_account_id: externalOwnerAccountId,
			hash_external_account_id: hashExternalAccountId,
			...params
		}
	} = useQueryParams();

	const paramsString = JSON.stringify(params);

	const startTraining = useCallback(
		(
			settings: AgentIntroTrainingSettings,
			sourceType: AgentIntroSourceType,
			source: string
		) => {
			if (
				!source ||
				!settings.connectorIdn ||
				!settings.clientSecret ||
				!settings.externalId
			) {
				return;
			}

			setExternalId(settings.externalId);

			setCustomerData({
				contact_fullname: isValid('contact_fullname', fullName) ? fullName : '',
				contact_email: isValid('contact_email', email) ? email : '',
				contact_phone: phoneNumber ?? '',
				promo_code: promoCode ?? '',
				industry: PARTNER_INDUSTRY || industry,
				country_code: countryCode ?? ''
			});

			const effectivePartnerIdn = queryPartnerIdn || partner_idn;
			const effectiveMemberIdn = queryPartnerMemberIdn || member_idn;

			setPartnerIdn(effectivePartnerIdn);
			setMemberIdn(effectiveMemberIdn);

			sendActorEvent(
				{
					client_secret: settings.clientSecret,
					connector_idn: settings.connectorIdn,
					event_idn: TalkingHeadActorEventIdn.OnboardingStarted,
					external_id: settings.externalId,
					arguments: buildTrainingStartPayload({
						source,
						sourceType,
						paramsString,
						partner_idn: effectivePartnerIdn,
						partner_member_idn: effectiveMemberIdn,
						industry,
						referral,
						fullName,
						email,
						phoneNumber,
						googleMapUrl,
						language,
						countryCode,
						externalAccountNumber,
						accountManagers,
						technicalOwners,
						notifications,
						comment,
						externalOwnerAccountId,
						hashExternalAccountId,
						userAgent: USER_AGENT_VALUE
					})
				},
				{
					onSettled: disableLoading,
					onSuccess: () => {
						setSearchParams({}, { replace: true });
						setScrapingStatus(TScrapingStatus.STARTED);
						if (runtimeConfig.isRemoteBuild) {
							setAgentStep(AgentCreationStep.TRAINING);
							setTrainingStep(AgentTrainingStep.Onboarding);
							return;
						}
						const isVibeFlow =
							partnerMetadata.isVibeCreatorConfirmationFlowEnabled();
						if (isVibeFlow) {
							setBusinessSource(googleMapUrl || websiteUrl);
						}
						navigate(
							buildNavigatePath(
								isVibeFlow ? PATHS.businessConfirmation : PATHS.agentTraining
							)
						);
					},
					onError: (error) => {
						setScrapingStatus(TScrapingStatus.INITIAL);
						if (isApiError(error)) {
							mantineNotification.show({
								'data-testid': 'api-error-notification',
								title: localization.translate('common', 'error'),
								message: localization.translate(
									'common',
									'something_wrong_select_location_again'
								),
								color: 'red'
							});
						}
					}
				}
			);
		},
		[
			localization,
			partner_idn,
			member_idn,
			setSearchParams,
			setScrapingStatus,
			navigate,
			setExternalId,
			setCustomerData,
			disableLoading,
			email,
			fullName,
			googleMapUrl,
			industry,
			paramsString,
			phoneNumber,
			promoCode,
			referral,
			sendActorEvent,
			countryCode,
			setMemberIdn,
			setPartnerIdn,
			language,
			externalAccountNumber,
			accountManagers,
			technicalOwners,
			notifications,
			comment,
			setTrainingStep,
			setAgentStep,
			queryPartnerIdn,
			queryPartnerMemberIdn,
			externalOwnerAccountId,
			hashExternalAccountId,
			setBusinessSource,
			websiteUrl
		]
	);

	const submitTrainingSource = useCallback(
		(source: string, sourceType: AgentIntroSourceType) => {
			enableLoading();
			mutateAsync(undefined, {
				onSuccess: ({ externalId, clientSecret, connectorIdn }) => {
					agentTrainingRealtime.init(
						{
							clientSecret,
							externalId,
							connectorIdn
						},
						AgentCommand.OnboardingFinished
					);

					startTraining(
						{ externalId, clientSecret, connectorIdn },
						sourceType,
						source
					);
				}
			});
		},
		[startTraining, mutateAsync, enableLoading]
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: should run only one time
	useEffect(() => {
		if (googleMapUrl || websiteUrl) {
			submitTrainingSource(
				googleMapUrl || websiteUrl,
				googleMapUrl
					? AgentIntroSourceType.GoogleMap
					: AgentIntroSourceType.Website
			);
		}
	}, []);

	return {
		startTraining: submitTrainingSource,
		isPending
	};
};

export default useTraining;
